import { getMediaItemUrl } from '@/Components/posts/utils';
import { Analytics } from '@/Telemetry';
import { MediaItemSizeEnum, type VertexSearchAgentInputType } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import { Avatar } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';

import type { RouterIds } from '@/Router';
import { useVertexSearchAgentQuery } from '@/graphql/queries/GCP.query';
import { RelayEnvironment } from '@/graphql/relay-env';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import styles from './SearchBar.module.css';

type Props = {
  disabled?: boolean;
};

const SearchBar = ({ disabled }: Props) => {
  const znglyInfo = useAppStore((state) => state.znglyInfo);
  const canDoAiSearch = useAppStore((state) => state.info?.canDoAiSearch);
  const pageType = useAppStore((state) => state.pageType);
  const navigate = useNavigate();

  const searchQueryParam = useSearch({
    strict: false,
    select(state) {
      return state?.s;
    },
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const isFetchingQuery = useSearchQuery();

  const placeholder = useMemo(() => {
    if (!!znglyInfo?.searchPlaceholderAi && canDoAiSearch) {
      return znglyInfo?.searchPlaceholderAi;
    }
    if (!!znglyInfo?.searchPlaceholder) {
      return znglyInfo?.searchPlaceholder;
    }
    return 'Search';
  }, [znglyInfo?.searchPlaceholder, znglyInfo?.searchPlaceholderAi, canDoAiSearch]);

  const avatarUrl = useMemo(() => {
    const avatar = getMediaItemUrl(znglyInfo?.searchImage, MediaItemSizeEnum.Medium) ?? false;
    if (!!znglyInfo?.searchImageAi && canDoAiSearch) {
      const aiSearchImage = getMediaItemUrl(znglyInfo?.searchImageAi, MediaItemSizeEnum.Medium);

      if (!!aiSearchImage) {
        return aiSearchImage;
      }
    }

    return avatar;
  }, [znglyInfo?.searchImage, znglyInfo?.searchImageAi, canDoAiSearch]);

  const isDisabled = useMemo(() => {
    return disabled || isFetchingQuery;
  }, [disabled, isFetchingQuery]);

  const onSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    if (isDisabled) return;
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const value = formData.get('s') as string;

    Analytics.push('search', {
      eventaction: 'search',
      value: value,
    });

    let to: RouterIds | null = '/';
    if ((['hub', 'znglist'] as Array<typeof pageType>).includes(pageType)) {
      to = null;
    }

    navigate({
      ...(to ? { to } : {}),
      // @ts-ignore
      search: (prev) => ({
        ...prev,
        s: value,
      }),
    });
  };

  // update the search input value when the search query changes
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.value = searchQueryParam ?? '';
    }
  }, [searchQueryParam]);

  return (
    <div className={styles.searchContainer}>
      <form onSubmit={onSubmitHandler} nonce={window.znRuntime?.cspStrictValue}>
        <input
          disabled={isDisabled}
          ref={searchInputRef}
          type="text"
          placeholder={placeholder}
          name="s"
          id="s"
          style={{
            ...(avatarUrl ? { paddingLeft: '40px' } : {}),
          }}
        />

        {Array.from(new URLSearchParams(window.location.search).entries()).map(([key, value]) => {
          if (key !== 's') {
            return <input type="hidden" value={value} name={key} key={`${key}_${value}`} />;
          }
        })}

        {!!avatarUrl ? (
          <Avatar
            src={avatarUrl}
            sx={{
              position: 'absolute',
              top: '6px',
              left: '4px',
              width: '30px',
              height: '30px',
            }}
          />
        ) : null}

        <button
          disabled={isDisabled}
          type="submit"
          id="searchsubmit"
          aria-label="Search"
          className={styles.searchButton}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
        >
          {!isFetchingQuery ? (
            <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x={0} y={0} viewBox="0 0 512.005 512.005">
              <title id="search-icon-title">Search Icon</title>
              <g>
                <g>
                  <path
                    d="M505.749,475.587l-145.6-145.6c28.203-34.837,45.184-79.104,45.184-127.317c0-111.744-90.923-202.667-202.667-202.667
											  S0,90.925,0,202.669s90.923,202.667,202.667,202.667c48.213,0,92.48-16.981,127.317-45.184l145.6,145.6
											  c4.16,4.16,9.621,6.251,15.083,6.251s10.923-2.091,15.083-6.251C514.091,497.411,514.091,483.928,505.749,475.587z
											  M202.667,362.669c-88.235,0-160-71.765-160-160s71.765-160,160-160s160,71.765,160,160S290.901,362.669,202.667,362.669z"
                  />
                </g>
              </g>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-loader"
              style={{ width: '24px', height: '24px' }}
            >
              <title id="search-icon-title">Search Icon</title>
              <line x1="12" y1="2" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="22" />
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
              <line x1="2" y1="12" x2="6" y2="12" />
              <line x1="18" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
};

const useSearchQuery = () => {
  const [fetchQuery, isFetchingQuery] = useVertexSearchAgentQuery();
  const pageType = useAppStore((state) => state.pageType);
  const canDoAiSearch = useAppStore((state) => state.info?.canDoAiSearch);

  const setVertexSearch = useAppStore((state) => state.setVertexSearchAgent);
  const setBarView = useAppStore((state) => state.setBarView);
  const setIsLoadingVertexSearch = useAppStore((state) => state.setIsLoadingVertexSearch);

  const hasFetched = useRef(false);

  const searchParams = useSearch({
    strict: false,
    select(state) {
      return {
        s: state?.s,
        categories: state?.categories,
        nav: state?.nav,
      };
    },
  });

  useEffect(() => {
    setIsLoadingVertexSearch(isFetchingQuery);

    return () => {
      setIsLoadingVertexSearch(false);
    };
  }, [isFetchingQuery]);

  useEffect(() => {
    if (!canDoAiSearch) {
      return;
    }

    if (!searchParams?.s || searchParams?.s === '') {
      if (hasFetched.current) {
        setVertexSearch(null);
        hasFetched.current = false;
        setBarView('list');
      }

      return;
    }

    if (hasFetched.current) {
      return;
    }

    const vertexArgs: VertexSearchAgentInputType = {
      query: searchParams?.s,
      ...(!!searchParams?.categories?.length && { categoryIn: searchParams?.categories }),
      pageType,
    };

    if (pageType === 'znglist') {
      vertexArgs.znglistId = window.location.pathname.split('/')[2];
    }

    if (pageType === 'hub') {
      vertexArgs.hubId = window.location.pathname.split('/')[2];
      vertexArgs.hubPageId = searchParams?.nav;
    }

    hasFetched.current = true;
    fetchQuery(
      {
        where: { vertex: vertexArgs },
      },
      {
        environment: RelayEnvironment,
        cacheConfig: {
          fetchPolicy: 'store-or-network',
        },
        onComplete(data, errors) {
          if (!!errors?.length) {
            toast.error('An error occurred while searching');
            return;
          }

          setVertexSearch(data?.vertexSearchAgent);
          setBarView('grid');
        },
      }
    );
  }, [searchParams?.s, searchParams?.categories, pageType, canDoAiSearch]);

  useEffect(() => {
    return () => {
      setVertexSearch(null);
    };
  }, []);

  return isFetchingQuery;
};

export default SearchBar;
